/**
 * Catches a render-phase crash in the document tree and reports it to the host
 * instead of letting `createRoot` unmount to a silently blank shadow root.
 *
 * Reliably catches synchronous throws during render -- e.g. `renderContract()`
 * (called from `useMemo` in `DocumentViewer`) rejecting a malformed contract.
 * It does NOT catch every possible failure: errors thrown asynchronously (from
 * inside a promise, a timeout, or certain effect timings in TipTap's internal
 * editor construction) happen outside React's render/commit cycle and are
 * invisible to an error boundary by design. This is a real improvement over
 * today's total silence, not a guarantee of catching everything that can go
 * wrong in the editor.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ERROR_CODES, EVENTS } from '../constants/element';

import type { EmitEvent } from '../types/element';
import type { DocumentErrorEvent } from '../types/events';

interface DocumentWorkspaceErrorBoundaryProps {
  children: ReactNode;
  emit: EmitEvent;
}

interface DocumentWorkspaceErrorBoundaryState {
  hasError: boolean;
}

export class DocumentWorkspaceErrorBoundary extends Component<
  DocumentWorkspaceErrorBoundaryProps,
  DocumentWorkspaceErrorBoundaryState
> {
  state: DocumentWorkspaceErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): DocumentWorkspaceErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    const message = error instanceof Error ? error.message : String(error);

    this.props.emit<DocumentErrorEvent>(EVENTS.DOCUMENT_ERROR, {
      code: ERROR_CODES.RENDER_FAILURE,
      message,
      context: { componentStack: info.componentStack ?? undefined },
    });
  }

  render(): ReactNode {
    // Render nothing rather than a fallback UI: this element has no design
    // system of its own to draw one in, and an empty shadow root is at least
    // an honest, silent-but-reported failure rather than a half-broken editor.
    return this.state.hasError ? null : this.props.children;
  }
}
